<template>
  <Dialog
    v-model:visible="visibleProxy"
    modal
    position="top"
    appendTo="body"
    :baseZIndex="1200"
    :closable="true"
    :dismissableMask="true"
    :draggable="false"
    class="settings-modal-dialog"
    :pt="dialogPassThrough"
    @hide="onDialogHide"
  >
    <template #header>
      <div class="crc-form-dialog-header">
        <div
          class="crc-form-dialog-header__icon bg-emerald-50 dark:bg-emerald-500/15 text-emerald-600 dark:text-emerald-400"
          aria-hidden="true"
        >
          <i class="pi pi-cog" />
        </div>
        <div class="crc-form-dialog-header__text">
          <div class="crc-form-dialog-header__title">Settings</div>
          <div class="crc-form-dialog-header__subtitle">Workspace administration</div>
        </div>
      </div>
    </template>

    <div v-if="availableSections.length" class="settings-modal__body">
      <aside class="settings-modal__sidebar settings-modal__sidebar--desktop">
        <div class="settings-modal__nav-scroll">
          <nav class="settings-modal__nav" aria-label="Settings sections">
            <SettingsNavItem
              v-for="section in availableSections"
              :key="section.key"
              :label="section.label"
              :icon="section.icon"
              :active="section.key === activeSection"
              @select="setActiveSection(section.key)"
            />
          </nav>
        </div>
        <div class="settings-modal__version" aria-label="Application version">
          Version {{ appVersionLabel }}
        </div>
      </aside>

      <div class="settings-modal__sidebar settings-modal__sidebar--mobile">
        <nav class="settings-modal__nav-pills" aria-label="Settings sections">
          <button
            v-for="section in availableSections"
            :key="section.key"
            type="button"
            class="settings-modal__pill"
            :class="{ 'settings-modal__pill--active': section.key === activeSection }"
            @click="setActiveSection(section.key)"
          >
            {{ section.label }}
          </button>
        </nav>
      </div>

      <main class="settings-modal__content">
        <component
          :is="activeSectionComponent"
          v-if="visibleProxy && activeSectionComponent && activeSectionMeta"
          :key="activeSectionMeta.key"
          :section="activeSectionMeta"
          @close-settings="close"
        />
      </main>
    </div>

    <div v-else class="settings-modal__empty">
      <Message severity="warn" :closable="false">
        You do not have access to any administration sections.
      </Message>
    </div>

    <template #footer>
      <div class="settings-modal__footer">
        <span class="settings-modal__version settings-modal__version--mobile" aria-label="Application version">
          Version {{ appVersionLabel }}
        </span>
      </div>
    </template>
  </Dialog>
</template>

<script setup>
import { computed, watch } from 'vue';
import Dialog from 'primevue/dialog';
import Message from 'primevue/message';
import SettingsNavItem from './SettingsNavItem.vue';
import { crcFormDialogPtNoScroll } from '@/components/common/dialog/crcDialogPt';
import { useGlobalSettings } from '../composables/useGlobalSettings';
import { useSettingsPermissions } from '../composables/useSettingsPermissions';
import { formatAppVersionLabel } from '@/constants/appMeta';
import '../styles/settingsOrgSection.css';

defineOptions({ name: 'SettingsModal' });

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:visible']);

const { activeSection, close, setActiveSection } = useGlobalSettings();
const { availableSections, isSectionAvailable } = useSettingsPermissions();

const visibleProxy = computed({
  get: () => props.visible,
  set: (value) => emit('update:visible', value),
});

const dialogPassThrough = {
  ...crcFormDialogPtNoScroll,
  content: { class: '!p-0 flex flex-col min-h-0 overflow-hidden' },
};

const activeSectionMeta = computed(() => {
  const key = activeSection.value;
  if (key && isSectionAvailable(key)) {
    return availableSections.value.find((section) => section.key === key) || null;
  }
  return availableSections.value[0] || null;
});

const activeSectionComponent = computed(() => activeSectionMeta.value?.component || null);

const appVersionLabel = formatAppVersionLabel();

const syncActiveSection = () => {
  const available = availableSections.value;
  if (!available.length) {
    if (activeSection.value != null) {
      setActiveSection(null);
    }
    return;
  }

  const current = activeSection.value;
  if (current && available.some((section) => section.key === current)) {
    return;
  }

  setActiveSection(available[0].key);
};

watch(
  () => props.visible,
  (visible) => {
    if (visible) {
      syncActiveSection();
    }
  }
);

watch(availableSections, () => {
  if (props.visible) {
    syncActiveSection();
  }
});

const onDialogHide = () => {
  close();
};
</script>

<style scoped>
.settings-modal__body {
  display: flex;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.settings-modal__sidebar {
  flex-shrink: 0;
  background: var(--crc-content-bg);
  border-right: 1px solid var(--crc-panel-border, var(--surface-border));
}

.settings-modal__sidebar--desktop {
  display: flex;
  flex-direction: column;
  width: 220px;
  min-height: 0;
  padding: 0;
  overflow: hidden;
}

.settings-modal__nav-scroll {
  flex: 1 1 auto;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  scrollbar-gutter: stable;
  padding: 0.75rem 0.75rem 0.375rem;
}

.settings-modal__sidebar--mobile {
  display: none;
  width: 100%;
  border-right: none;
  border-bottom: 1px solid var(--crc-panel-border, var(--surface-border));
  padding: 0.75rem 1rem;
  overflow-x: auto;
  overflow-y: hidden;
}

.settings-modal__nav {
  width: 100%;
}

.settings-modal__version {
  flex: 0 0 auto;
  padding: 0.625rem 1rem 0.75rem;
  border-top: 1px solid var(--crc-panel-border, var(--surface-border));
  font-size: 0.6875rem;
  line-height: 1.25;
  color: var(--crc-text-muted, var(--text-color-secondary));
  letter-spacing: 0.02em;
}

.settings-modal__footer {
  display: none;
  width: 100%;
  justify-content: flex-start;
}

.settings-modal__version--mobile {
  display: inline-block;
}

.settings-modal__nav-pills {
  display: flex;
  gap: 0.5rem;
  flex-wrap: nowrap;
  min-width: min-content;
}

.settings-modal__pill {
  border: 1px solid var(--crc-panel-border, var(--surface-border));
  border-radius: 999px;
  padding: 0.375rem 0.875rem;
  background: var(--crc-bar-bg, #fff);
  color: var(--crc-text-muted, var(--text-color-secondary));
  font-size: 0.8125rem;
  font-weight: 500;
  white-space: nowrap;
  cursor: pointer;
}

.settings-modal__pill--active {
  border-color: var(--crc-green, #1d9e75);
  color: var(--crc-green, #1d9e75);
  background: var(--crc-green-soft, rgba(29, 158, 117, 0.12));
}

.settings-modal__content {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  scrollbar-gutter: stable;
  background: var(--crc-bar-bg, var(--surface-card));
}

.settings-modal__empty {
  padding: 1.5rem;
}

@media (max-width: 960px) {
  .settings-modal__body {
    flex-direction: column;
  }

  .settings-modal__sidebar--desktop {
    display: none;
  }

  .settings-modal__sidebar--mobile {
    display: block;
  }

  .settings-modal__footer {
    display: flex;
  }
}
</style>

<style>
.settings-modal-dialog {
  --settings-modal-width: min(1100px, calc(100vw - 32px));
  --settings-modal-height: min(760px, calc(100vh - 48px));
  width: var(--settings-modal-width) !important;
  height: var(--settings-modal-height) !important;
  max-height: var(--settings-modal-height) !important;
}

.settings-modal-root {
  display: flex !important;
  flex-direction: column !important;
  height: 100% !important;
  max-height: var(--settings-modal-height, min(760px, calc(100vh - 48px))) !important;
}

:deep(.crc-form-dialog.settings-modal-dialog),
:deep(.settings-modal-dialog .crc-form-dialog) {
  display: flex !important;
  flex-direction: column !important;
  height: 100% !important;
  max-height: var(--settings-modal-height, min(760px, calc(100vh - 48px))) !important;
}

:deep(.settings-modal-dialog .p-dialog-content) {
  display: flex !important;
  flex-direction: column !important;
  flex: 1 !important;
  min-height: 0 !important;
  overflow: hidden !important;
}

:deep(.settings-modal-dialog .p-dialog-footer) {
  display: none;
  padding: 0.625rem 1rem 0.75rem;
}

.settings-modal-dialog .settings-org-section__body {
  scrollbar-gutter: stable;
}

@media (max-width: 960px) {
  :deep(.settings-modal-dialog .p-dialog-footer) {
    display: block;
  }
}
</style>
